import React from 'react';

// Constants for lyrics fitting
const MIN_LYRICS_FONT_SIZE = 10;
const MAX_LETTER_SPACING_REDUCTION = -0.5;

export interface FittedLyricsSimpleProps {
  lyrics: string;
  onLyricChange: (newLyric: string) => void;
  baseFontSize: number;
  measureIndex: number;
}

const FittedLyricsSimple: React.FC<FittedLyricsSimpleProps> = ({ 
  lyrics, 
  onLyricChange, 
  baseFontSize, 
  measureIndex 
}) => {
  const textRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  // Debug log to check if baseFontSize is updating
  React.useEffect(() => {
    console.log(`Lyrics font size updated for measure ${measureIndex}: ${baseFontSize}px`);
  }, [baseFontSize, measureIndex]);

  // Measure container width
  React.useLayoutEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) return;

    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });
    observer.observe(containerElement);
    return () => observer.disconnect();
  }, []);

  // Apply fitting logic
  React.useLayoutEffect(() => {
    const textElement = textRef.current;
    if (!textElement || containerWidth === 0 || !lyrics) return;

    // Reset styles to natural state
    textElement.style.letterSpacing = 'normal';
    textElement.style.fontSize = `${baseFontSize}px`;
    textElement.style.transform = 'scaleX(1)';
    
    // Force content update
    textElement.textContent = lyrics;

    const effectiveContainerWidth = containerWidth - 8;
    let textWidth = textElement.scrollWidth;

    if (textWidth <= effectiveContainerWidth) {
      return;
    }

    // Stage 1: Reduce letter spacing
    for (let ls = -0.1; ls >= MAX_LETTER_SPACING_REDUCTION; ls -= 0.1) {
      textElement.style.letterSpacing = `${ls.toFixed(1)}px`;
      textWidth = textElement.scrollWidth;
      if (textWidth <= effectiveContainerWidth) {
        return;
      }
    }

    // Stage 2: Reduce font size
    for (let fs = baseFontSize - 1; fs >= MIN_LYRICS_FONT_SIZE; fs -= 1) {
      textElement.style.fontSize = `${fs}px`;
      textWidth = textElement.scrollWidth;
      if (textWidth <= effectiveContainerWidth) {
        return;
      }
    }

    // Stage 3: Compress with scaleX
    if (textWidth > effectiveContainerWidth) {
      const scale = effectiveContainerWidth / textWidth;
      textElement.style.transform = `scaleX(${scale})`;
    }
  }, [lyrics, baseFontSize, containerWidth]);

  return (
    <div ref={containerRef} className="text-gray-400 overflow-hidden flex justify-center">
      <div
        ref={textRef}
        className="cursor-text whitespace-nowrap"
        style={{ transformOrigin: 'center' }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={() => {
          const newLyric = prompt('Edit lyrics:', lyrics || '');
          if (newLyric !== null) {
            onLyricChange(newLyric);
          }
        }}
      >
        {lyrics}
      </div>
    </div>
  );
};

export default FittedLyricsSimple;
